import type {
  DomainPropertySummary,
  DomainPropertyIR,
  DomainValidationRuleSummary,
  ManifestValidationRuleInput,
} from "@granular-software/metamodel-core";
import { defineMetamodelPackage } from "@granular-software/metamodel-core";

function describeRule(rule: DomainValidationRuleSummary) {
  if (rule.message) return rule.message;
  if (rule.stringValue !== undefined)
    return `${rule.operator} ${JSON.stringify(rule.stringValue)}`;
  if (rule.numberValue !== undefined)
    return `${rule.operator} ${rule.numberValue}`;
  if (rule.booleanValue !== undefined)
    return `${rule.operator} ${String(rule.booleanValue)}`;
  return rule.operator;
}

function normalizeRule(rule: any): DomainValidationRuleSummary | null {
  const operator = typeof rule?.operator === "string" ? rule.operator : "";
  if (operator.length === 0) return null;

  const normalized: DomainValidationRuleSummary = { operator };

  if (typeof rule?.string_value === "string") {
    normalized.stringValue = rule.string_value;
  }
  if (typeof rule?.number_value === "number") {
    normalized.numberValue = rule.number_value;
  }
  if (typeof rule?.boolean_value === "boolean") {
    normalized.booleanValue = rule.boolean_value;
  }
  if (typeof rule?.message === "string") {
    normalized.message = rule.message;
  }

  return normalized;
}

export function buildValidationRuleFieldMutations(
  fieldPath: string,
  rules?: ManifestValidationRuleInput[],
) {
  return (rules || []).map((rule) => {
    const args = [
      `operator: ${JSON.stringify(rule.operator)}`,
      rule.stringValue !== undefined
        ? `string_value: ${JSON.stringify(rule.stringValue)}`
        : null,
      rule.numberValue !== undefined
        ? `number_value: ${rule.numberValue}`
        : null,
      rule.booleanValue !== undefined
        ? `boolean_value: ${rule.booleanValue}`
        : null,
      rule.message ? `message: ${JSON.stringify(rule.message)}` : null,
    ]
      .filter(Boolean)
      .join(", ");

    return {
      label: `add validation rule on ${fieldPath}`,
      query: `mutation { at(path: ${JSON.stringify(fieldPath)}) { add_validation_rule(${args}) { operator } } }`,
    };
  });
}

export const validationRuleMetamodelPackage = defineMetamodelPackage({
  id: "validation_rule",
  docs: {
    fieldRows: [
      {
        key: "validate",
        description:
          "Array of validation rules. Operators include `eq`, `neq`, `gt`, `gte`, `lt`, `lte`, `true`, `false`, `regex`, `contains`, `not_contains`, `starts_with`, `ends_with`.",
      },
    ],
  },
  graphql: {
    typeDefs: [
      `
      type ValidationRuleMetamodel {
        model: Model!
        operator: String!
        string_value: String
        number_value: Float
        boolean_value: Boolean
        message: String
      }

      type ValidationError {
        code: String!
        path: String!
        message: String!
      }

      type ValidationResult {
        ok: Boolean!
        errors: [ValidationError!]!
      }

      extend type Model {
        validation_rules: [ValidationRuleMetamodel!]!
        validate_instance: ValidationResult!
      }

      extend type ModelMutation {
        add_validation_rule(
          operator: String!
          string_value: String
          number_value: Float
          boolean_value: Boolean
          message: String
        ): ValidationRuleMetamodel
        validate_instance: ValidationResult
      }
      `,
    ],
    createResolvers({ run }) {
      return {
        ValidationRuleMetamodel: {
          model: (value) => value.model,
          operator: (value) => value.operator,
          string_value: (value) => value.string_value || null,
          number_value: (value) => value.number_value ?? null,
          boolean_value: (value) => value.boolean_value ?? null,
          message: (value) => value.message || null,
        },
        ValidationError: {
          code: (value) => value.code,
          path: (value) => value.path,
          message: (value) => value.message,
        },
        ValidationResult: {
          ok: (value) => value.ok,
          errors: (value) => value.errors,
        },
        Model: {
          validation_rules: async (ant) => await run(ant.validation_rules()),
          validate_instance: async (ant) => await run(ant.validate_instance()),
        },
        ModelMutation: {
          add_validation_rule: async (ant, config) => {
            const model = await run(ant.add_validation_rule(config));
            return { model, ...config };
          },
          validate_instance: async (ant) => await run(ant.validate_instance()),
        },
      };
    },
  },
  manifest: {
    buildFieldMutations(fieldPath, spec) {
      return buildValidationRuleFieldMutations(fieldPath, spec.validate);
    },
  },
  summary: {
    selections: {
      propertyFields: [
        `validation_rules { operator string_value number_value boolean_value message }`,
      ],
    },
    readPropertySummary(rawProperty) {
      const rules = Array.isArray(rawProperty.validation_rules)
        ? rawProperty.validation_rules
            .map(normalizeRule)
            .filter((rule): rule is DomainValidationRuleSummary =>
              Boolean(rule),
            )
        : [];

      return {
        validationRules: rules,
      };
    },
  },
  domain: {
    applyToPropertyIR(
      propertyIR: DomainPropertyIR,
      propertySummary: DomainPropertySummary,
    ) {
      const rules = propertySummary.validationRules || [];
      if (rules.length === 0) return propertyIR;

      return {
        ...propertyIR,
        docs: [
          ...propertyIR.docs,
          `Validation rules: ${rules.map(describeRule).join("; ")}.`,
        ],
      };
    },
  },
});
